import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from '@vortx/shared-ui';

export default function PrivateRoute({ children }) {
    const { currentUser, userProfile, loading } = useAuth();
    const location = useLocation();

    if (loading) {
        console.warn('[DEBUG] PrivateRoute: still loading, showing Loading...', { path: location.pathname });
        return <div>Loading...</div>;
    }

    if (!currentUser) {
        console.warn('[DEBUG] PrivateRoute: no currentUser, redirecting to /login', { path: location.pathname });
        return <Navigate to="/login" replace />;
    }

    // Redirect users without Site DNA access
    if (userProfile?.noAccess) {
        console.warn('[DEBUG] PrivateRoute: noAccess, redirecting to /no-access', { path: location.pathname });
        return <Navigate to="/no-access" replace />;
    }

    // Redirect users with incomplete profiles to complete their profile
    // Skip if already on the complete-profile page to avoid infinite redirect
    if (userProfile && userProfile.profile_completed === false && location.pathname !== '/complete-profile') {
        console.warn('[DEBUG] PrivateRoute: incomplete profile, redirecting to /complete-profile', { path: location.pathname });
        return <Navigate to="/complete-profile" replace />;
    }

    return children;
}

/**
 * Module-specific route guard.
 * Checks that the user has access to a specific product module.
 *
 * @param {string} requiredProduct - The product key to check access for ('site_dna', 'flux_ems', 'flux_phd')
 */
export function ModuleRoute({ children, requiredProduct }) {
    const { currentUser, userProfile, loading } = useAuth();
    const location = useLocation();

    if (loading) {
        return <div>Loading...</div>;
    }

    if (!currentUser) {
        return <Navigate to="/login" replace />;
    }

    // Redirect users without any product access
    if (userProfile?.noAccess) {
        return <Navigate to="/no-access" replace />;
    }

    // Check module-specific access
    if (requiredProduct && !userProfile?.products?.[requiredProduct]) {
        // Redirect to dashboard if user doesn't have access to this module
        return <Navigate to="/dashboard" replace />;
    }

    // Redirect users with incomplete profiles to complete their profile
    if (userProfile && userProfile.profile_completed === false && location.pathname !== '/complete-profile') {
        return <Navigate to="/complete-profile" replace />;
    }

    return children;
}
